import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Supabase browser client.
 *
 * Keys are fetched at runtime from /config.json (served by the web Worker)
 * rather than baked into the bundle at build time. That means rotating a key is
 * a Worker variable change, not a rebuild and redeploy.
 *
 * The PROMISE is cached, not the resolved client. Caching the resolved value
 * still allows two concurrent callers to both start a create, which produces the
 * "Multiple GoTrueClient instances detected" warning and, worse, two clients
 * racing on the same storage key.
 */

interface RuntimeConfig {
    readonly supabaseUrl: string;
    readonly supabasePublishableKey: string;
}

let configPromise: Promise<RuntimeConfig> | null = null;
let clientPromise: Promise<SupabaseClient> | null = null;

async function getConfig(): Promise<RuntimeConfig> {
    if (configPromise === null) {
        configPromise = fetch('/config.json', { cache: 'no-store' })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(`config.json: ${res.status}`);
                }
                return (await res.json()) as RuntimeConfig;
            })
            .catch((err: unknown) => {
                configPromise = null;
                throw err;
            });
    }
    return configPromise;
}

export function getSupabase(): Promise<SupabaseClient> {
    if (clientPromise === null) {
        clientPromise = getConfig()
            .then((cfg) =>
                createClient(cfg.supabaseUrl, cfg.supabasePublishableKey, {
                    auth: { persistSession: true, autoRefreshToken: true },
                }),
            )
            .catch((err: unknown) => {
                clientPromise = null;
                throw err;
            });
    }
    return clientPromise;
}

/**
 * Synchronous access to the current access token.
 *
 * `apiFetch` runs on every request and must not await `getSession()` each time.
 * The auth provider mirrors the session here whenever it changes.
 */
let cachedToken: string | null = null;

export function setCachedToken(token: string | null): void {
    cachedToken = token;
}

export function getCachedToken(): string | null {
    return cachedToken;
}

/** Fetch a guaranteed-current token, refreshing if needed. Used to retry a 401 once. */
export async function getFreshToken(): Promise<string | null> {
    const sb = await getSupabase();
    const { data } = await sb.auth.getSession();
    return data.session?.access_token ?? null;
}
